<template>
  <BaseModal
    :container-class="[...MODAL_CONTAINER_WIDTH_CLASSES, 'tw:max-h-[50vw]!']"
    content-class="tw:p-6"
    header-class="tw:p-4"
    :title="getLocaleText('external_items_bulk_add_modal.title')"
    @close="modalClosed"
  >
    <template #header>
      <div class="tw:flex tw:items-center tw:gap-2">
        <BackButton
          data-testid="back-button"
          @click="backButtonClicked"
        />

        <span class="tw:text-lg tw:font-bold tw:flex-1 tw:text-center">
          {{ getLocaleText("external_items_bulk_add_modal.title") }}
        </span>
      </div>
    </template>

    <p class="tw:text-base tw:font-bold">
      {{ getLocaleText("external_items_bulk_add_modal.article_urls_header") }}
    </p>
    <p>
      {{
        getLocaleText("external_items_bulk_add_modal.article_urls_description")
      }}
    </p>

    <BaseInputText
      v-slot="slotProps"
      v-model="urlsInput"
      data-testid="urls-input"
      :height="266"
      input-classes="tw:text-nowrap tw:overflow-auto tw:p-2! tw:block"
      new-design
      type="textarea"
    >
      <template v-if="flowState !== FlowState.Editing">
        <div
          :class="slotProps?.class"
          :style="slotProps?.style"
        >
          <UrlDataRow
            v-for="urlData in verifiedUrlsData!"
            :key="urlData.url"
            data-testid="url-data"
            :disable-buttons="donePending"
            :url-data="urlData"
            @add-url="emit('addUrl', urlData.url)"
          />
        </div>
      </template>
    </BaseInputText>

    <div class="tw:my-4 tw:flex tw:justify-between">
      <p
        aria-hidden="true"
        data-testid="urls-counter"
      >
        {{ urlsCount }}/{{ URLS_MAX_COUNT }}
      </p>
      <span
        aria-atomic="true"
        aria-live="polite"
        class="tw:sr-only"
        data-testid="urls-counter-status"
        role="status"
      >
        {{ urlsCountAnnouncement }}
      </span>
      <BaseButton
        v-if="flowState === FlowState.VerificationCompleted"
        color="primary"
        data-testid="edit-urls-button"
        :disabled="editUrlsButtonDisabled"
        :label="getLocaleText('external_items_bulk_add_modal.edit_urls_button')"
        new-design
        @click="editUrlsButtonClicked"
      />
      <BaseButton
        v-else
        color="primary"
        data-testid="verify-button"
        :disabled="verifyButtonDisabled"
        :label="verifyButtonLabel"
        new-design
        @click="verifyButtonClicked"
      />
    </div>

    <UrlsTotalsByStatus
      v-if="verifiedUrlsData"
      class="tw:mb-4"
      data-testid="totals-by-status"
      :urls-data="verifiedUrlsData!"
    />

    <p class="tw:mb-8">
      {{ getLocaleText("external_items_bulk_add_modal.possible_delay_info") }}
    </p>

    <template #buttons>
      <BaseButton
        color="outline"
        data-testid="cancel-button"
        :disabled="donePending"
        :label="getLocaleText('global.cancel')"
        new-design
        @click="cancelButtonClicked"
      />
      <BaseButton
        color="primary"
        data-testid="done-button"
        :disabled="doneButtonDisabled"
        :label="getLocaleText('global.done')"
        :loading="donePending"
        new-design
        @click="doneButtonClicked"
      />
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { debounce, isNil, some, trim } from "lodash-es";
import { computed, onBeforeUnmount, ref, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseButton, BaseInputText } from "shared/components/base";
import BaseModal from "shared/components/core/modals/BaseModal.vue";
import useModal from "shared/composables/useModal";
import { splitIntoLines } from "shared/helpers/string";
import type { Nullable } from "shared/types";

import BackButton from "../shared/BackButton.vue";
import { MODAL_CONTAINER_WIDTH_CLASSES } from "../shared/constants";
import { type UrlData, type UrlValueChange } from "../shared/types";

import { URLS_MAX_COUNT } from "./shared";
import UrlDataRow from "./UrlDataRow.vue";
import UrlsTotalsByStatus from "./UrlsTotalsByStatus.vue";
import useUrlsLimitEnforcing from "./useUrlsLimitEnforcing";

export type ExternalItemsBulkAddModalProps = {
  donePending?: boolean;
  streamId: number;
  urlValueChange?: Nullable<UrlValueChange>;
  verificationInProgress?: boolean;
  verifiedUrlsData?: Nullable<UrlData[]>;
};

const props = defineProps<ExternalItemsBulkAddModalProps>();

defineOptions({
  name: "ExternalItemsBulkAddModal",
});

const emit = defineEmits<{
  addUrl: [string];
  cancel: [];
  done: [];
  goBack: [];
  modalClosed: [];
  resetVerificationResults: [];
  urlValueChangeHandled: [UrlValueChange];
  verifyUrls: [string[]];
}>();

const { close } = useModal("ExternalItemsBulkAddModal");

const urlsInput = ref<string>("");

const urlsTokenized = computed<string[]>(() =>
  (splitIntoLines(urlsInput.value) as string[])
    .map(trim)
    .filter((line) => line.length > 0)
);

const urlsCount = computed<number>(() => urlsTokenized.value.length);

useUrlsLimitEnforcing({ urlsInput, urlsCount });

// Announced only once typing settles: a status update per line typed or pasted
// would queue up and talk over the user. Debounced on the input rather than the
// count, so the first character of a new URL doesn't trigger an announcement
// while the rest of it is still being typed.
const URLS_COUNT_ANNOUNCEMENT_DELAY = 1000;

const urlsCountAnnouncement = ref<string>("");

const announceUrlsCount = debounce(() => {
  urlsCountAnnouncement.value = getLocaleText(
    urlsCount.value >= URLS_MAX_COUNT
      ? "external_items_bulk_add_modal.urls_count_status_limit_reached"
      : "external_items_bulk_add_modal.urls_count_status",
    { urlsCount: urlsCount.value, maxUrlsCount: URLS_MAX_COUNT }
  );
}, URLS_COUNT_ANNOUNCEMENT_DELAY);

watch(urlsInput, () => announceUrlsCount());

onBeforeUnmount(() => announceUrlsCount.cancel());

enum FlowState {
  Editing = "Editing",
  VerificationCompleted = "VerificationCompleted",
  VerificationInProgress = "VerificationInProgress",
}

const flowState = computed<FlowState>(() => {
  if (isNil(props.verifiedUrlsData)) return FlowState.Editing;

  if (props.verificationInProgress) return FlowState.VerificationInProgress;

  return FlowState.VerificationCompleted;
});

const verifyButtonDisabled = computed(
  () =>
    urlsCount.value === 0 ||
    flowState.value !== FlowState.Editing ||
    props.donePending
);

const verifyButtonLabel = computed<string>(() => {
  const suffix = {
    [FlowState.Editing]: "start",
    [FlowState.VerificationInProgress]: "in_progress",
    [FlowState.VerificationCompleted]: "", // should not happen (keeping the key for type correctness)
  }[flowState.value];

  return getLocaleText(`external_items_bulk_add_modal.verify_button.${suffix}`);
});

const someUrlsAddedInCurrentFlow = computed<boolean>(() =>
  some(props.verifiedUrlsData, "addedInCurrentFlow")
);

const editUrlsButtonDisabled = computed<boolean>(
  () => someUrlsAddedInCurrentFlow.value || props.donePending
);

const doneButtonDisabled = computed<boolean>(
  () => flowState.value !== FlowState.VerificationCompleted || props.donePending
);

function backButtonClicked() {
  emit("goBack");
  close();
}

function cancelButtonClicked() {
  emit("cancel");
}

function doneButtonClicked() {
  emit("done");
}

function modalClosed() {
  emit("modalClosed");
}

function verifyButtonClicked() {
  emit("verifyUrls", urlsTokenized.value);
}

function editUrlsButtonClicked() {
  emit("resetVerificationResults");
}

// URL may be edited externally (in "manual add" modal), let's reflect that in urls input text
// (at the moment of writing this user shouldn't be able to go back to "edit" mode
// after having added the external item successfully, so they wouldn't notice if we didn't do it,
// but let's keep the data internally consistent to avoid bugs in the future)
watch(
  () => props.urlValueChange,
  (change) => {
    if (!change) return;

    const inputLines: string[] = splitIntoLines(urlsInput.value);

    // replace only the first occurrence - if there are more of them,
    // they're marked as duplicates and could have not been edited
    const matchingLineIndex = inputLines.findIndex(
      (line) => trim(line) === change.oldValue
    );

    if (matchingLineIndex !== -1) {
      inputLines[matchingLineIndex] = inputLines[matchingLineIndex].replace(
        change.oldValue,
        change.newValue
      );

      urlsInput.value = inputLines.join("\n");
    }

    emit("urlValueChangeHandled", change);
  }
);
</script>
